import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';
const fetchWithAuth = (url, options = {}) => {
  return fetch(url, { ...options, headers: { ...(options.headers || {}), 'x-token': getToken() } });
};

const DASHBOARD_COMPONENT_TYPES = [
  { type: 'statusCard', label: '📍 今日状态卡片', desc: '各场景响应时间卡片', defaultConfig: {} },
  { type: 'comparisonTable', label: '🔄 迁移前后对比表', desc: '硬件迁移前后的平均响应时长对比', defaultConfig: {} },
  { type: 'weeklyChart', label: '📈 近7天趋势图', desc: '柱状图+在线人数折线图(双轴)', defaultConfig: { height: 130 } },
  { type: 'trafficGrid', label: '📊 红绿灯表格', desc: '近7天系统响应速度表格', defaultConfig: {} },
  { type: 'onlineBar', label: '📊 近7日在线人数', desc: '按时点(9/11/14/16)4色区分', defaultConfig: { height: 200 } },
  { type: 'pieChart', label: '🥧 响应时间分布饼图', desc: '近7天各场景响应时间占比', defaultConfig: { height: 200 } },
  { type: 'monthlyAvg', label: '📈 月度平均响应时间', desc: '按月统计各场景平均响应时长', defaultConfig: { height: 200 } },
  { type: 'monthlyAvgBar', label: '📊 全年各月日均在线', desc: '每月日均在线人数+平均线', defaultConfig: { height: 200 } },
  { type: 'monthlyGroupBar', label: '📊 全年各时段平均在线', desc: '各月分时点分组柱状图', defaultConfig: { height: 200 } },
];

const REPORT_COMPONENT_TYPES = [
  { type: 'aiAnalysis', label: '🤖 监控分析', desc: '勾选指标生成无建议纯客观分析', defaultConfig: { height: 250 } },
  { type: 'statusCard', label: '📍 今日状态卡片', desc: '各场景响应时间卡片', defaultConfig: {} },
  { type: 'comparisonTable', label: '🔄 迁移前后对比表', desc: '硬件迁移前后的平均响应时长对比', defaultConfig: {} },
  { type: 'weeklyChart', label: '📈 近7天趋势图', desc: '柱状图+在线人数折线图(双轴)', defaultConfig: { height: 130 } },
  { type: 'trafficGrid', label: '📊 红绿灯表格', desc: '近7天系统响应速度表格', defaultConfig: {} },
  { type: 'onlineBar', label: '📊 在线人数柱状图', desc: '按时点(9/11/14/16)4色区分', defaultConfig: { height: 200 } },
  { type: 'pieChart', label: '🥧 响应时间分布饼图', desc: '近7天各场景响应时间占比', defaultConfig: { height: 200 } },
  { type: 'monthlyAvg', label: '📈 月度平均响应时间', desc: '按月统计各场景平均响应时长', defaultConfig: { height: 200 } },
  { type: 'monthlyAvgBar', label: '📊 全年各月日均在线', desc: '每月日均在线人数+平均线', defaultConfig: { height: 200 } },
  { type: 'monthlyGroupBar', label: '📊 全年各时段平均在线', desc: '各月分时点分组柱状图', defaultConfig: { height: 200 } },
];

export default function MonitorLayoutConfig() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [layout, setLayout] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const isDashboard = activeTab === 'dashboard';
  const componentTypes = isDashboard ? DASHBOARD_COMPONENT_TYPES : REPORT_COMPONENT_TYPES;
  const layoutKey = isDashboard ? 'dashboardLayout' : 'reportLayout';

  useEffect(() => {
    if (projectId) loadConfig();
  }, [projectId]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/monitor/config/${projectId}`);
      const data = await res.json();
      if (data.success && data.config) {
        setConfig(data.config);
        setProjectName(data.config.projectName || '');
        // 根据当前tab加载对应的布局
        const layoutData = isDashboard ? (data.config.dashboardLayout || []) : (data.config.reportLayout || []);
        setLayout(layoutData);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // 从可用组件列表添加
  const addComponent = (typeDef) => {
    const newItem = { type: typeDef.type, config: { ...typeDef.defaultConfig } };
    setLayout([...layout, newItem]);
  };

  // 删除组件
  const removeComponent = (idx) => {
    const newLayout = [...layout];
    newLayout.splice(idx, 1);
    setLayout(newLayout);
  };

  // 上移
  const moveUp = (idx) => {
    if (idx === 0) return;
    const newLayout = [...layout];
    [newLayout[idx-1], newLayout[idx]] = [newLayout[idx], newLayout[idx-1]];
    setLayout(newLayout);
  };

  // 下移
  const moveDown = (idx) => {
    if (idx >= layout.length - 1) return;
    const newLayout = [...layout];
    [newLayout[idx], newLayout[idx+1]] = [newLayout[idx+1], newLayout[idx]];
    setLayout(newLayout);
  };

  // 更新配置项
  const updateConfig = (idx, key, value) => {
    const newLayout = [...layout];
    newLayout[idx] = { ...newLayout[idx], config: { ...newLayout[idx].config, [key]: value } };
    setLayout(newLayout);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const updatedConfig = { ...config, [layoutKey]: layout };
      const res = await fetchWithAuth(`${API_URL}/api/monitor/config/${projectId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedConfig)
      });
      const data = await res.json();
      if (data.success) {
        setSaveMsg(`✅ ${isDashboard ? '监控主页' : '报告页'}布局已保存`);
        setTimeout(() => setSaveMsg(''), 3000);
      } else {
        setSaveMsg('❌ 保存失败: ' + (data.error || '未知错误'));
      }
    } catch (e) {
      setSaveMsg('❌ 保存失败: ' + e.message);
    }
    setSaving(false);
  };

  // 找出当前已选的类型
  const usedTypes = new Set(layout.map(item => item.type));

  if (loading) return <div className="loading">加载中...</div>;

  return (
    <div className="monitor-config">
      <div className="page-header">
        <button onClick={() => navigate('/monitor')} className="btn-back">← 返回</button>
        <h1>🎨 布局配置 - {projectName || projectId}</h1>
      </div>

      {/* Tab切换 */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 16, gap: 4 }}>
        <button onClick={() => { setActiveTab('dashboard'); setLayout(config?.dashboardLayout || []); }}
          style={{ padding: '8px 16px', border: 'none', borderBottom: activeTab === 'dashboard' ? '2px solid #8B5CF6' : '2px solid transparent', background: 'transparent', color: activeTab === 'dashboard' ? '#8B5CF6' : '#666', fontWeight: activeTab === 'dashboard' ? 600 : 400, cursor: 'pointer' }}>
          🖥️ 监控主页布局
        </button>
        <button onClick={() => { setActiveTab('report'); setLayout(config?.reportLayout || []); }}
          style={{ padding: '8px 16px', border: 'none', borderBottom: activeTab === 'report' ? '2px solid #8B5CF6' : '2px solid transparent', background: 'transparent', color: activeTab === 'report' ? '#8B5CF6' : '#666', fontWeight: activeTab === 'report' ? 600 : 400, cursor: 'pointer' }}>
          📄 报告页布局
        </button>
      </div>

      <div className="config-form">
        {/* 可用组件列表 */}
        <div className="form-section">
          <h3>📦 可用组件</h3>
          <p style={{ fontSize: '12px', color: '#888', marginBottom: '10px' }}>点击添加组件到{isDashboard ? '监控主页' : '报告页'}:</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {componentTypes.filter(t => !usedTypes.has(t.type)).map(t => (
              <button key={t.type} onClick={() => addComponent(t)}
                style={{ fontSize: '12px', padding: '6px 14px', border: '1px dashed #8B5CF6', color: '#8B5CF6', background: '#f5f3ff', borderRadius: '20px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                + {t.label}
              </button>
            ))}
            {componentTypes.every(t => usedTypes.has(t.type)) && (
              <div style={{ fontSize: '12px', color: '#999' }}>所有组件已添加</div>
            )}
          </div>
        </div>

        {/* 已选布局项 */}
        <div className="form-section">
          <h3>📐 {isDashboard ? '监控主页' : '报告页'}布局顺序 <span style={{ fontSize: '11px', color: '#999', fontWeight: 400 }}>(从上到下排列)</span></h3>
          {layout.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
              暂无布局项,从上方"可用组件"添加
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
            {layout.map((item, idx) => {
              const typeDef = componentTypes.find(t => t.type === item.type);
              return (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                  {/* 序号 */}
                  <span style={{ fontSize: '12px', color: '#999', minWidth: '20px' }}>{idx + 1}.</span>

                  {/* 组件信息 */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: '13px' }}>
                      {typeDef ? typeDef.label : item.type}
                    </div>
                    <div style={{ fontSize: '11px', color: '#999' }}>{typeDef?.desc || ''}</div>
                  </div>

                  {/* 高度配置(仅图表类) */}
                  {(item.type === 'weeklyChart' || item.type === 'onlineBar' || item.type === 'pieChart' || item.type === 'monthlyAvg') && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <label style={{ fontSize: '11px', color: '#888' }}>高:</label>
                      <input type="number" value={item.config?.height || 200}
                        onChange={e => updateConfig(idx, 'height', parseInt(e.target.value) || 200)}
                        style={{ width: '60px', padding: '2px 6px', fontSize: '12px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                        min={80} max={600} />
                      <span style={{ fontSize: '11px', color: '#999' }}>px</span>
                    </div>
                  )}

                  {/* 操作按钮 */}
                  <button onClick={() => moveUp(idx)} disabled={idx === 0}
                    style={{ fontSize: '12px', padding: '2px 8px', border: '1px solid #d9d9d9', borderRadius: '4px', background: '#fff', cursor: idx === 0 ? 'default' : 'pointer', opacity: idx === 0 ? 0.3 : 1 }}>↑</button>
                  <button onClick={() => moveDown(idx)} disabled={idx >= layout.length - 1}
                    style={{ fontSize: '12px', padding: '2px 8px', border: '1px solid #d9d9d9', borderRadius: '4px', background: '#fff', cursor: idx >= layout.length - 1 ? 'default' : 'pointer', opacity: idx >= layout.length - 1 ? 0.3 : 1 }}>↓</button>
                  <button onClick={() => removeComponent(idx)}
                    style={{ fontSize: '12px', padding: '2px 8px', border: '1px solid #ff4d4f', borderRadius: '4px', background: '#fff2f0', color: '#cf1322', cursor: 'pointer' }}>✕</button>
                </div>
              );
            })}
          </div>
        </div>

        {/* 保存按钮 */}
        <div className="form-actions">
          <button onClick={handleSave} className="btn-primary" disabled={saving}>
            {saving ? '保存中...' : '保存布局'}
          </button>
          <button onClick={() => navigate('/monitor')} className="btn-secondary">取消</button>
          {saveMsg && (
            <span style={{ marginLeft: 10, fontSize: '12px', color: saveMsg.startsWith('✅') ? '#52c41a' : '#ff4d4f' }}>
              {saveMsg}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
